import useSWR from "swr";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { useAdminHeader } from "../AdminShell";
import { apiGetEnvelope } from "../api";
import { formatDate, PageError, PAGE_SIZE, stageLabel } from "../ui-helpers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export function RunsPage() {
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);
  const filter = params.get("filter") ?? "";
  const offset = (page - 1) * PAGE_SIZE;
  const path = `/admin/api/tables/stage_runs/rows?limit=${PAGE_SIZE}&offset=${offset}&filter=${encodeURIComponent(filter)}`;
  const { data, error } = useSWR(path, () => apiGetEnvelope<Record<string, unknown>[]>(path), { refreshInterval: 8000 });
  const rows = data?.data ?? [];
  const total = Number(data?.meta?.count ?? rows.length);
  function patch(next: Record<string, string | null>) {
    const out = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) value ? out.set(key, value) : out.delete(key);
    setParams(out);
  }
  useAdminHeader({
    title: "Runs",
    breadcrumbs: [{ label: "Runs" }],
    actions: <div className="relative w-80"><Search className="absolute left-2 top-2 size-4 text-muted-foreground" /><Input className="pl-8" defaultValue={filter} placeholder="Filter runs" onKeyDown={(event) => { if (event.key === "Enter") patch({ filter: event.currentTarget.value, page: "1" }); }} /></div>,
  }, [filter]);
  if (error) return <PageError error={error} />;
  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-4">
        <h1 className="text-lg font-semibold">Stage runs</h1>
        <p className="mt-1 text-sm text-muted-foreground">Click a run to inspect logs.</p>
      </div>
      {!data ? <Skeleton className="h-96" /> : (
        <>
          <div className="overflow-auto rounded-md border">
            <Table>
              <TableHeader><TableRow><TableHead>Run</TableHead><TableHead>City</TableHead><TableHead>Stage</TableHead><TableHead>State</TableHead><TableHead>Progress</TableHead><TableHead>Started</TableHead></TableRow></TableHeader>
              <TableBody>
                {rows.map((run) => (
                  <TableRow key={String(run.id)} className="cursor-pointer">
                    <TableCell><Link className="font-medium hover:underline" to={`/runs/${String(run.id)}`}>{String(run.id)}</Link></TableCell>
                    <TableCell>{String(run.citySlug ?? "")}</TableCell>
                    <TableCell>{stageLabel(run.stage)}</TableCell>
                    <TableCell><Badge variant="outline">{String(run.state ?? "")}</Badge></TableCell>
                    <TableCell>{String(run.done ?? 0)} / {String(run.total ?? 0)}</TableCell>
                    <TableCell>{formatDate(run.startedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span>{offset + 1}-{Math.min(offset + rows.length, total)} of {total}</span>
            <div className="flex gap-2"><Button variant="outline" disabled={page <= 1} onClick={() => patch({ page: String(page - 1) })}>Previous</Button><Button variant="outline" disabled={offset + PAGE_SIZE >= total} onClick={() => patch({ page: String(page + 1) })}>Next</Button></div>
          </div>
        </>
      )}
    </div>
  );
}
